import { Link, useLocation } from "react-router-dom";
import { useRef, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { getPendingPrintErrorCount } from "../../actions/printError";
import { getPrinters } from "../../actions/printer";
import { getNewPartnerCount } from "../../actions/partners";
import { healthOf } from "../../utils/printerHealth";
import { canAccess } from "../../utils/access";
import { navGroupsFor } from "./navItems";

const PENDING_REFRESH_MS = 60 * 1000;

const itemClass =
  "inline-flex items-center w-full text-sm font-semibold transition-colors duration-150 hover:text-gray-800 dark:hover:text-gray-200";

const Sidebar = ({
  auth: { user, userLoading },
  collapseShow,
  setCollapseShow,
  pendingPrintErrors,
  newPartnerRequests,
  printers,
  getPrinters,
  getPendingPrintErrorCount,
  getNewPartnerCount,
}) => {
  const ref = useRef();
  const { pathname } = useLocation();
  const showsPrinters = canAccess(user, "printer");
  const showsPrintErrors = canAccess(user, "printErrors");
  const showsPartners = canAccess(user, "partners");

  useEffect(() => {
    if (!showsPrinters) return;
    getPrinters();
    const refresh = setInterval(getPrinters, PENDING_REFRESH_MS);
    return () => clearInterval(refresh);
  }, [showsPrinters, getPrinters]);

  // Roles without print errors would get a 403.
  // Kept current while a page stays open, like the Printer page's refresh.
  useEffect(() => {
    if (!showsPrintErrors) return;
    getPendingPrintErrorCount();
    const refresh = setInterval(getPendingPrintErrorCount, PENDING_REFRESH_MS);
    return () => clearInterval(refresh);
  }, [showsPrintErrors, getPendingPrintErrorCount]);

  useEffect(() => {
    if (!showsPartners) return;
    getNewPartnerCount();
    const refresh = setInterval(getNewPartnerCount, PENDING_REFRESH_MS);
    return () => clearInterval(refresh);
  }, [showsPartners, getNewPartnerCount]);
  const handleClick = (e) => {
    if (ref.current && !ref.current.contains(e.target)) {
      setCollapseShow(!collapseShow);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  });

  if (userLoading || !user) return null;
  const groups = navGroupsFor(user);
  if (groups.length === 0) return null;

  // A kiosk that has gone offline needs someone to go out to it, so it counts too
  const badges = {
    printErrors: pendingPrintErrors,
    partners: newPartnerRequests,
    printer: printers.filter((printer) =>
      ["offline", "error", "printerProblem"].includes(healthOf(printer))
    ).length,
  };

  const renderItem = (item) => {
    const active = (item.paths || [item.to]).some((path) =>
      pathname.startsWith(path)
    );
    const badge = badges[item.section];
    return (
      <li key={item.to} className="relative px-6 py-3">
        {active && (
          <span
            className="absolute inset-y-0 left-0 w-1 bg-purple-600 rounded-tr-lg rounded-br-lg"
            aria-hidden="true"
          ></span>
        )}
        <Link
          to={item.to}
          aria-current={active ? "page" : undefined}
          className={
            active ? itemClass + " text-gray-800 dark:text-gray-100" : itemClass
          }
        >
          <svg
            className="w-5 h-5"
            aria-hidden="true"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={item.icon}
            />
          </svg>
          <span className="ml-4">{item.label}</span>
          {badge > 0 && (
            <span className="px-2 py-0.5 ml-auto text-xs font-semibold leading-tight text-yellow-700 bg-yellow-100 rounded-full dark:bg-yellow-700 dark:text-yellow-100">
              {badge}
            </span>
          )}
        </Link>
      </li>
    );
  };

  // The same content goes in the desktop bar and the mobile drawer
  const nav = (
    <div className="py-4 text-gray-500 dark:text-gray-400">
      <Link
        to={groups[0].items[0].to}
        className="ml-6 text-lg font-bold text-gray-800 dark:text-gray-200"
      >
        CloudPrint
      </Link>
      <nav className="mt-6">
        {groups.map((group, i) => (
          <div key={group.title || i}>
            {group.title && (
              <p className="px-6 pt-4 pb-1 text-xs font-semibold tracking-wider text-gray-400 uppercase dark:text-gray-500">
                {group.title}
              </p>
            )}
            <ul>{group.items.map(renderItem)}</ul>
          </div>
        ))}
      </nav>
    </div>
  );

  return (
    <>
      <aside className="z-20 hidden w-64 overflow-y-auto bg-white dark:bg-gray-800 md:block flex-shrink-0">
        {nav}
      </aside>
      <div
        className={
          collapseShow
            ? "fixed inset-0 z-10 flex items-end bg-black bg-opacity-50 sm:items-center sm:justify-center hidden"
            : "fixed inset-0 z-10 flex items-end bg-black bg-opacity-50 sm:items-center sm:justify-center"
        }
      ></div>
      {!collapseShow && (
        <aside
          className="fixed inset-y-0 z-20 flex-shrink-0 w-64 mt-16 overflow-y-auto bg-white dark:bg-gray-800 md:hidden"
          ref={ref}
        >
          {nav}
        </aside>
      )}
    </>
  );
};

Sidebar.propTypes = {
  auth: PropTypes.object.isRequired,
  pendingPrintErrors: PropTypes.number,
  newPartnerRequests: PropTypes.number,
  printers: PropTypes.array.isRequired,
  getPrinters: PropTypes.func.isRequired,
  getPendingPrintErrorCount: PropTypes.func.isRequired,
  getNewPartnerCount: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  printers: state.printer.printers,
  pendingPrintErrors: state.printError.pendingCount,
  newPartnerRequests: state.partners.newCount,
});

export default connect(mapStateToProps, {
  getPendingPrintErrorCount,
  getPrinters,
  getNewPartnerCount,
})(Sidebar);
